/* eslint-disable no-restricted-imports */
import {
	ProofRequirementModel,
	UserUpdateRequestModel,
	UserUpdateRequestOptions,
} from 'src/public.services';
import { ValidationConstants } from '../constants/constants';
import { EnrollmentInfoPaperworkModel } from '../services/api/students/enrollment/student-enrollment-info-response.model';
import { UserAddressModel } from '../services/utility/utility-models/address.model';
import { hasValue, isObject } from './compare';
import { convertToString } from './convert';

export function getCurrentSchoolYear(changeOverMonth = 6) {
	const now = new Date();
	const year = now.getFullYear();
	const month = now.getMonth();

	const startYear = month >= changeOverMonth ? year : year - 1;
	const endYear = startYear + 1;

	const startDate = new Date(startYear, changeOverMonth, 1); // First day of the changeover month of the start year

	// Last day of the month before the changeover month of the end year
	const endDate = new Date(endYear, changeOverMonth, 0);

	return {
		start: startDate,
		end: endDate,
	};
}

export function hasSuperMasquerade() {
	return document.querySelector('#block-switchuser') != null;
}

export function superMasqueradeAsUser(userId: number) {
	try {
		(
			document.querySelector(
				'#block-switchuser input[data-drupal-selector="edit-userid"]',
			) as HTMLInputElement
		).value = `a (${userId})`;
		(
			document.querySelector(
				'#block-switchuser button[data-drupal-selector="edit-submit"]',
			) as HTMLButtonElement
		).click();
	} catch {
		// eslint-disable-next-line no-alert
		alert('Failed to Super Masquerade');
	}
}

export function getLaptopNeedsInfo(obj: boolean[], requestNeeds: string[]) {
	if (obj == null) return '';
	return obj
		?.map((x, i) => (x ? requestNeeds[i] : null))
		.filter((x) => x != null)
		.map((x) => `{color:#ff5630}*${x}*{color}`)
		.join('\n');
}

export function buildDescription(
	formObject: Partial<{
		uid: number;
		name: string;
		phone: string;
		email: string;
		notes: string;
		requestNeeds: string;
		returnNeeds: boolean[];
		address: UserAddressModel;
		isReturn: boolean;
		grade: string;
		programTitle: string;
	}>,
	protectedEmail: string,
	requestNeeds: string[],
	paperwork: EnrollmentInfoPaperworkModel,
) {
	const value = formObject;
	const address = formObject.address;
	const hostUrl = `${globalThis.location.protocol}//${globalThis.location.host}`;
	const studentName = `${value.name}`;

	let laptopNeedsInfo: string;
	if (formObject.isReturn) {
		laptopNeedsInfo = getLaptopNeedsInfo(value.returnNeeds, requestNeeds);
	} else if (value.requestNeeds === 'Chromebook and MiFi') {
		laptopNeedsInfo = `${value.requestNeeds}`;
	} else {
		laptopNeedsInfo = `{color:#ff5630}*${value.requestNeeds}*{color}`;
	}

	let paperworkUrl = paperwork?.url;
	const regX = new RegExp(ValidationConstants.urlRegex, 'i');
	if (paperwork && paperworkUrl && !regX.test(paperworkUrl)) {
		paperworkUrl = `[Financial Responsibility Form|${window.location.protocol}//${window.location.host}${paperworkUrl}]`;
	} else {
		paperworkUrl = '';
	}

	return `*Student Information*
${studentName} - ${formObject.uid}
[Student Link|${hostUrl}/app/student-activity/${formObject.uid}]
${paperworkUrl}

*Request Needs*
${laptopNeedsInfo}

*Contact Information*
${buildEmail(protectedEmail, 'contact')}${value.phone}

*Mailing Information*
${value.name}
${address.street}${address.street2 ? `\n${address.street2}` : ''}
${address.city}, ${address.state} ${address.zip}

*Additional Information*
${buildEmail(value.email, 'additional')}${value.notes ? `{color:#ff5630}*${value.notes.trim()}*{color}` : '-'}

Program Name: ${value.programTitle ? `${value.programTitle}` : '-'}
Grade Level: ${value.grade ? `${value.grade}` : '-'}`;
}

export function buildEmail(email: string, location: 'contact' | 'additional') {
	if (location === 'contact') {
		if (hasValue(email)) {
			return `${email}\n`;
		} else return '';
	} else if (location === 'additional') {
		if (hasValue(email)) {
			return `Personal Email: ${email}\n`;
		} else return '';
	}

	return '';
}

export function isAddress(value: unknown): boolean {
	if (!isObject(value) || value == null) return false;

	return 'street' in value && 'city' in value && 'state' in value;
}

function isEmptyAddress(value: Partial<UserAddressModel>): boolean {
	return (
		(value.street ?? '').trim() === '' &&
		(value.street2 ?? '').trim() === '' &&
		(value.city ?? '').trim() === '' &&
		(value.state ?? '').trim() === '' &&
		(value.zip ?? '').trim() === '' &&
		(value.country_code ?? '').trim() === ''
	);
}

export function isSameAddress(
	address1: Partial<UserAddressModel>,
	address2: Partial<UserAddressModel>,
): boolean {
	const addr1Empty = !isAddress(address1) || isEmptyAddress(address1);
	const addr2Empty = !isAddress(address2) || isEmptyAddress(address2);

	if (addr1Empty && addr2Empty) return true;
	if (addr1Empty || addr2Empty) return false;

	const sameStreet =
		(address1.street ?? '').trim() === (address2.street ?? '').trim();
	const sameStreet2 =
		(address1.street2 ?? '').trim() === (address2.street2 ?? '').trim();
	const sameCity =
		(address1.city ?? '').trim() === (address2.city ?? '').trim();
	const sameState =
		(address1.state ?? '').trim() === (address2.state ?? '').trim();
	const sameZip = (address1.zip ?? '').trim() === (address2.zip ?? '').trim();
	const sameCountry =
		(address1.country_code ?? '').trim() ===
		(address2.country_code ?? '').trim();

	return (
		sameStreet && sameStreet2 && sameCity && sameState && sameZip && sameCountry
	);
}

export function isPhone(value: unknown): boolean {
	if (!isObject(value) || value == null) return false;
	return (
		('phone' in value || 'value' in value) && 'type' in value && 'sms' in value
	);
}

export function isSamePhone(
	phone1: Partial<unknown>,
	phone2: Partial<unknown>,
): boolean {
	const phone1Empty = !isPhone(phone1);
	const phone2Empty = !isPhone(phone2);
	if (phone1Empty && phone2Empty) return true;
	if (phone1Empty || phone2Empty) return false;
	const value1 = phone1?.['phone'] ? phone1?.['phone'] : phone1?.['value'];
	const value2 = phone2?.['phone'] ? phone2?.['phone'] : phone2?.['value'];
	// compare type, prime, active, weight
	const arr = ['type', 'prim', 'active', 'weight', 'sms'];
	const differences = arr.filter((prop) => {
		if (
			phone1.hasOwnProperty(prop) &&
			phone2.hasOwnProperty(prop) &&
			phone1[prop] !== phone2[prop]
		) {
			return true;
		}
		return false;
	});
	return (
		differences.length === 0 &&
		(convertToString(value1) ?? '').trim() ===
			(convertToString(value2) ?? '').trim()
	);
}

/**
 * This gets pending updates and lists the new, old, and proof requirement for each field that has a pending update.
 * It is used to display pending updates to the user and to determine which fields have pending updates that require proof.
 * @param updates
 * @param updateOptions
 * @returns Map<string, {old: unknown; new: unknown; proof_requirement: ProofRequirementModel}>
 */
export function getPendingField(
	updates: UserUpdateRequestModel[],
	updateOptions: UserUpdateRequestOptions,
) {
	const updatedStudentInfoMap = new Map<
		string,
		{
			old: unknown;
			new: unknown;
			proof_requirement: ProofRequirementModel;
		}
	>();

	if (updateOptions) {
		let update = null;
		let updateField = null;

		Object.keys(updateOptions).forEach((key) => {
			Object.keys(updateOptions[key]).forEach((field) => {
				updateField = field;
				update = null;
				updates.forEach((u) => {
					if (u.updates[key] != null && u.updates[key][field] != null) {
						update = u.updates[key][field];
						updateField = field;
					}
				});

				const proofRequirement =
					updateOptions[key][updateField]?.proof_requirement;
				if (proofRequirement) {
					if (updateField === 'profile_student_year') {
						proofRequirement['title'] = 'Grade';
					}
					if (updateField === 'dob') {
						proofRequirement['title'] = 'Birthdate';
						proofRequirement['description'] = 'Birthdate';
					} else {
						proofRequirement['title'] = updateOptions[key][updateField]?.title;
					}
				}

				updatedStudentInfoMap.set(updateField, {
					old: updateOptions[key][updateField]?.current_value
						? updateOptions[key][updateField]?.current_value?.value
							? updateOptions[key][updateField]?.current_value?.value
							: updateOptions[key][updateField]?.current_value
						: null,
					new: update?.new,
					proof_requirement: proofRequirement,
				});
			});
		});
	}

	return updatedStudentInfoMap;
}
